<!-- Modern Invoice Creation Form -->
<div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">

  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ isEditMode ? "Edit Invoice" : "Create Invoice" | translate }}
        </h1>
        <p class="text-gray-600">
          {{ isEditMode ? "Update invoice details" : "Create and manage invoices for your construction projects" }}
        </p>
      </div>

      <!-- Back Button -->
      <app-back-button
        label="Back to Invoices"
        link="/home/invoice-list">
      </app-back-button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex items-center justify-center py-12">
    <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
    <span class="ml-3 text-gray-600">Loading invoice data...</span>
  </div>

  <!-- Invoice Form Card -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden" *ngIf="!isLoading">
    <form
      [formGroup]="executionFrom"
      class="p-8"
      (ngSubmit)="onSubmit()"
    >
      <!-- Form Header -->
      <div class="pb-6 mb-6 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900">Invoice Details</h2>
        <p class="text-sm text-gray-500 mt-1">Fill in the invoice information below</p>
      </div>

      <!-- Common Fields Grid -->
      <div class="grid grid-cols-12 gap-x-6 gap-y-4">
        <ng-container *ngFor="let field of commonFields">

          <!-- Text Fields -->
          <div class="col-span-12 md:col-span-6" *ngIf="field.type === 'text' || field.type === 'currency' || field.type === 'number'">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ field.label }}
              <span *ngIf="field.required" class="text-red-500">*</span>
            </label>
            <mat-form-field appearance="outline" class="w-full">
              <input
                matInput
                [formControlName]="field.key"
                [type]="field.type === 'number' ? 'number' : 'text'"
                [placeholder]="'Enter ' + field.label"
              />
              <mat-icon matPrefix *ngIf="field.type === 'currency'" class="text-gray-400">currency_rupee</mat-icon>
              <mat-error *ngIf="executionFrom.get(field.key)?.hasError('required')">
                {{ field.label }} is required
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Select Fields -->
          <div class="col-span-12 md:col-span-6" *ngIf="field.type === 'select'">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ field.label }}
              <span *ngIf="field.required" class="text-red-500">*</span>
            </label>
            <mat-form-field appearance="outline" class="w-full">
              <mat-select
                [formControlName]="field.key"
                [placeholder]="'Select ' + field.label"
                (selectionChange)="field.onChange?.($event.value)"
              >
                <mat-option
                  *ngFor="let option of optionsMap[field.optionKey]"
                  [value]="option.value || option"
                >
                  {{ option.label || option }}
                </mat-option>
              </mat-select>
              <mat-error *ngIf="executionFrom.get(field.key)?.hasError('required')">
                {{ field.label }} is required
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Textarea Fields -->
          <div class="col-span-12" *ngIf="field.type === 'textarea'">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ field.label }}
              <span *ngIf="field.required" class="text-red-500">*</span>
            </label>
            <mat-form-field appearance="outline" class="w-full">
              <textarea
                matInput
                [formControlName]="field.key"
                rows="3"
                [placeholder]="'Enter ' + field.label"
              ></textarea>
              <mat-error *ngIf="executionFrom.get(field.key)?.hasError('required')">
                {{ field.label }} is required
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Date Fields -->
          <div class="col-span-12 md:col-span-6" *ngIf="field.type === 'date'">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ field.label }}
              <span *ngIf="field.required" class="text-red-500">*</span>
            </label>
            <mat-form-field appearance="outline" class="w-full">
              <input
                matInput
                [matDatepicker]="picker"
                [formControlName]="field.key"
                [placeholder]="'Select ' + field.label"
              />
              <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
              <mat-error *ngIf="executionFrom.get(field.key)?.hasError('required')">
                {{ field.label }} is required
              </mat-error>
            </mat-form-field>
          </div>
        </ng-container>
      </div>

      <!-- Domain-Specific Fields Section -->
      <div *ngIf="domainFields[selectedDomain] && domainFields[selectedDomain].length > 0" class="mt-8">
        <div class="pb-4 mb-6 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">{{ selectedDomain }} Specific Details</h3>
          <p class="text-sm text-gray-500 mt-1">Additional fields for {{ selectedDomain }} domain</p>
        </div>

        <div class="grid grid-cols-12 gap-x-6 gap-y-4">
          <ng-container *ngFor="let field of domainFields[selectedDomain]">
            <div class="col-span-12 md:col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                {{ field.label }}
                <span *ngIf="field.required" class="text-red-500">*</span>
              </label>
              <mat-form-field appearance="outline" class="w-full">
                <input
                  matInput
                  [formControlName]="field.key"
                  [type]="field.type === 'number' ? 'number' : 'text'"
                  [placeholder]="'Enter ' + field.label"
                />
                <mat-icon matPrefix *ngIf="field.type === 'currency'" class="text-gray-400">currency_rupee</mat-icon>
              </mat-form-field>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4 justify-end">
        <button
          mat-button
          type="button"
          (click)="onReset()"
          class="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
        >
          <mat-icon class="mr-2">refresh</mat-icon>
          Reset
        </button>

        <button
          mat-raised-button
          color="primary"
          type="submit"
          [disabled]="isSubmitting"
          class="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <mat-icon class="mr-2" *ngIf="!isSubmitting">save</mat-icon>
          <mat-icon class="mr-2 animate-spin" *ngIf="isSubmitting">refresh</mat-icon>
          {{ isSubmitting ? 'Saving...' : (isEditMode ? 'Update Invoice' : 'Create Invoice') }}
        </button>
      </div>
    </form>
  </div>
</div>
